import React, { useEffect, useState, Suspense } from 'react';
import './styles.scss';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { scheme } from './validation';
import {
  ComboboxData,
  Divider,
  Flex,
  Group,
  MultiSelect,
  Select,
  Text,
  TextInput
} from '@mantine/core';
import Button from '../../components/elements/Button';
import { DatePickerInput } from '@mantine/dates';
import * as Unicons from '@iconscout/react-unicons';
import dayjs from 'dayjs';
import localId from 'dayjs/locale/id';
const TextEditor = React.lazy(
  () => import('../../components/elements/TextEditor')
);
// import TextEditor from '../../components/elements/TextEditor';
import { MissionTypeList } from '../../constants/defaultValues/mission';
import { dateToISOString } from '../../utilities/renderDate';
import { convertToLocalTime } from '../../utilities/convertToLocaleTime';

interface FormMissionProps {
  type?: string;
  data?: any;
  loading?: boolean;
  listBusinessUnit?: ComboboxData;
  badgeList?: ComboboxData;
  onClose: () => void;
  handleSubmitData: (data: any) => void;
}

interface MasterMissionData {
  missionCategory?: string;
  missionName?: string;
  missionDescription?: string;
  missionType?: string;
  trigger?: any;
  targetAudience?: any[];
  badgeID?: string;
  rewardPoint?: any;
  rewardXP?: any;
  multiplierPoint?: any;
  pointExpired?: string;
  startDate?: string;
  endDate?: string;
  loginStreak?: number;
  missionID?: string;
}

const SELECT_ALL_VALUE = '__SELECT_ALL__';

const FormMission: React.FC<FormMissionProps> = ({
  onClose,
  loading,
  type,
  data,
  badgeList,
  listBusinessUnit,
  handleSubmitData
}) => {
  const [valuePointExpired, setValuePointExpired] = useState<Date | null>(null);
  const [valueStartDate, setValueStartDate] = useState<Date | null>(null);
  const [valueEndDate, setValueEndDate] = useState<Date | null>(null);
  const [selectedMissionType, setSelectedMissionType] = useState<string>('');

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<MasterMissionData>({
    defaultValues: {
      missionCategory: data?.missionCategory || '',
      missionName: data?.missionName || '',
      missionDescription: data?.missionDescription || '',
      missionType: data?.missionType || '',
      trigger: data?.trigger || null,
      targetAudience: data?.targetAudience || [],
      badgeID: data?.badgeID || null,
      rewardPoint: data?.rewardPoint || null,
      rewardXP: data?.rewardXP || null,
      multiplierPoint: data?.multiplierPoint || 1,
      pointExpired: data?.pointExpired || '',
      startDate: data?.startDate || '',
      endDate: data?.endDate || '',
      loginStreak: data?.loginStreak || 0
    },
    resolver: yupResolver(scheme)
  });

  useEffect(() => {
    if (
      type === 'edit' &&
      data &&
      listBusinessUnit.length > 0 &&
      badgeList.length > 0
    ) {
      const arrInput = [
        'missionCategory',
        'missionName',
        'missionDescription',
        'missionType',
        'trigger',
        'badgeID',
        'targetAudience',
        'rewardPoint',
        'rewardXP',
        'multiplierPoint',
        'pointExpired',
        'startDate',
        'endDate',
        'loginStreak'
      ];
      arrInput.forEach((item: any) => {
        if (data[item]) {
          setValue(item, data[item]);
        }
      });

      setValueStartDate(
        data.startDate
          ? dayjs(data.startDate + 'Z')
              .locale(localId)
              .toDate()
          : null
      );

      setSelectedMissionType(data.missionType);
      setValueEndDate(
        data?.endDate
          ? dayjs(data.endDate + 'Z')
              .locale(localId)
              .toDate()
          : null
      );
      setValuePointExpired(
        data?.pointExpired
          ? dayjs(data.pointExpired + 'Z')
              .locale(localId)
              .toDate()
          : null
      );
    }
  }, [data, type, listBusinessUnit]);

  const handleMissionTypeChange = (e, field) => {
    setSelectedMissionType(e);
    field.onChange(e);
  };

  const handleChangeDate = (date: any, type: string, field: any) => {
    field.onChange(date);
    if (type === 'startDate') setValueStartDate(date);
    if (type === 'endDate') setValueEndDate(date);
    if (type === 'pointExpired') setValuePointExpired(date);
    return;
  };

  const handleSubmitForm: SubmitHandler<MasterMissionData> = (values) => {
    const selectedTargetAudience = values.targetAudience.map((id) => {
      return listBusinessUnit.find((item: any) => item.value === id);
    });

    const arrParseInt = [
      'rewardPoint',
      'rewardXP',
      'multiplierPoint',
      'trigger'
    ];
    arrParseInt.forEach((item) => {
      if (values[item]) {
        values[item] = parseInt(values[item]);
      }
    });

    const arrToIsoString = ['endDate', 'startDate', 'pointExpired'];
    arrToIsoString.forEach((item) => {
      values[item] = dateToISOString(values[item]);
    });

    values.targetAudience = selectedTargetAudience;

    const keyValues = Object.keys(values);
    keyValues.forEach((key) => {
      if (key === 'loginStreak') {
        values[key] = parseInt(values[key] as any);
        return;
      }
      if (!values[key]) {
        values[key] = '';
        return;
      }
      if (key === 'targetAudience') {
        return;
      }

      values[key] = values[key].toString();
      return;
    });

    if (type === 'edit') {
      values.missionID = data?.missionID;
    }

    handleSubmitData(values);
  };

  const getBusinessUnitOptions = () => {
    return listBusinessUnit.filter(
      (item: any) => item.label !== 'UNCATEGORIZED'
    )
  }

  const targetAudienceData = (selected: string[] = []) => {
    const options = getBusinessUnitOptions();
    const allValues = options.map((item: any) => item.value);
    const isAllSelected =
      allValues.length > 0 && selected.length === allValues.length;

    return [
      {
        value: SELECT_ALL_VALUE,
        label: isAllSelected ? 'Unselect All' : 'Select All'
      },
      ...options
    ];
  };

  return (
    <>
      <section className="master-category-content__form">
        {' '}
        <Text className="text-sm-bold" mb={8}>
          Mission Information Detail
        </Text>
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Controller
            control={control}
            render={({ field }) => (
              <Select
                label={'Mission Category'}
                data={['Default Mission', 'Pick Mission']}
                placeholder={'Select Mission Category'}
                disabled={loading}
                withAsterisk
                searchable
                error={errors?.missionCategory?.message}
                {...field}
              />
            )}
            name="missionCategory"
          />
          <TextInput
            label="Mission Name"
            radius={10}
            withAsterisk
            placeholder="Input mission name"
            error={errors.missionName?.message}
            {...register('missionName')}
          />

          <Suspense fallback={<div>Loading...</div>}>
            <Controller
              control={control}
              render={({ field }) => (
                <TextEditor
                  height={200}
                  label="Mission Description"
                  withAsterik
                  error={errors.missionDescription?.message}
                  value={field.value}
                  onEditorChange={(value) => field.onChange(value)}
                />
              )}
              name="missionDescription"
            />
          </Suspense>
          <Controller
            control={control}
            render={({ field }) => (
              <Select
                label={'Mission Type'}
                data={MissionTypeList}
                placeholder={'Select Mission Type'}
                disabled={loading}
                value={selectedMissionType}
                onChange={(e) => handleMissionTypeChange(e, field)}
                withAsterisk
                searchable
                error={errors?.missionType?.message}
              />
            )}
            name="missionType"
          />
          {selectedMissionType === 'Login Mission' && (
            <TextInput
              label="Login Streak"
              radius={10}
              placeholder="Bonus tambahan point jika melakukan login streak"
              error={errors.loginStreak?.message}
              {...register('loginStreak')}
            />
          )}

          <TextInput
            label="Trigger"
            radius={10}
            withAsterisk
            type="number"
            placeholder="How many times to trigger the mission so user can get the badge?"
            error={errors.trigger?.message as string}
            {...register('trigger')}
          />
          <Divider my={16} />
          <Text className="text-sm-bold" mb={8}>
            Target Audience
          </Text>

          <Controller
            control={control}
            render={({ field }) => (
              <MultiSelect
                key={data?.missionID}
                label="Select Business Unit"
                placeholder="Pick value"
                data={targetAudienceData(field.value || [])}
                error={errors?.targetAudience?.message}
                withAsterisk
                searchable
                value={field.value || []}
                onChange={(value) => {
                  const allOptions = getBusinessUnitOptions().map((item: any) => item.value);

                  // Jika user memilih "Select All"
                  if (value.includes(SELECT_ALL_VALUE)) {
                    // Kalau sebelumnya belum semua kepilih → select semua
                    if (field.value?.length !== allOptions.length) {
                      field.onChange(allOptions);
                    } else {
                      // kalau semua sudah kepilih → unselect semua
                      field.onChange([]);
                    }
                    return;
                  }

                  // Normal select
                  field.onChange(value);
                }}
              />
            )}
            name="targetAudience"
          />
          <Divider my={16} />
          <Text className="text-sm-bold" mb={8}>
            Mission Reward
          </Text>

          <Controller
            control={control}
            render={({ field }) => (
              <Select
                label={'Badge'}
                data={badgeList}
                placeholder={'Select badge'}
                disabled={loading}
                // withAsterisk
                searchable
                error={errors?.badgeID?.message}
                value={field.value}
                {...field}
              />
            )}
            name="badgeID"
          />
          <Group justify="space-between" gap={16}>
            <TextInput
              label="Reward Point"
              radius={10}
              flex={1}
              withAsterisk
              type="number"
              placeholder="Input reward point"
              error={errors.rewardPoint?.message as string}
              {...register('rewardPoint')}
            />

            <TextInput
              label="Reward XP"
              radius={10}
              flex={1}
              withAsterisk
              type="number"
              placeholder="Input reward XP"
              error={errors.rewardXP?.message as string}
              {...register('rewardXP')}
            />
          </Group>
          <TextInput
            label="Multiplier Point"
            radius={10}
            withAsterisk
            maxLength={4}
            placeholder="Input multiplier point"
            error={errors.multiplierPoint?.message as string}
            {...register('multiplierPoint')}
          />
          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <DatePickerInput
                className={
                  errors.pointExpired?.message &&
                  'schedule-rater__form__selectinput--error'
                }
                flex={1}
                label="Point Expired Date"
                withAsterisk
                placeholder="Select date"
                onChange={(date) =>
                  handleChangeDate(date, 'pointExpired', field)
                }
                minDate={dayjs().toDate()}
                value={valuePointExpired}
                rightSection={<Unicons.UilCalendarAlt size={20} />}
                error={errors?.pointExpired?.message}
              />
            )}
            name="pointExpired"
          />
          <Divider my={16} />
          <Text className="text-sm-bold" mb={8}>
            Mission Schedule
          </Text>

          <Group justify="space-between" gap={16}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <DatePickerInput
                  className={
                    errors.startDate?.message &&
                    'schedule-rater__form__selectinput--error'
                  }
                  flex={1}
                  label="Start Date"
                  withAsterisk
                  placeholder="Select Date"
                  onChange={(date) =>
                    handleChangeDate(date, 'startDate', field)
                  }
                  minDate={dayjs().toDate()}
                  value={valueStartDate}
                  rightSection={<Unicons.UilCalendarAlt size={20} />}
                  error={errors?.startDate?.message}
                />
              )}
              name="startDate"
            />

            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <DatePickerInput
                  className={
                    errors.endDate?.message &&
                    'schedule-rater__form__selectinput--error'
                  }
                  flex={1}
                  label="End Date"
                  withAsterisk
                  placeholder="Select Date"
                  onChange={(date) => handleChangeDate(date, 'endDate', field)}
                  minDate={dayjs().toDate()}
                  value={valueEndDate}
                  rightSection={<Unicons.UilCalendarAlt size={20} />}
                  error={errors?.endDate?.message}
                />
              )}
              name="endDate"
            />
          </Group>
          <Flex justify="flex-end" align="center" gap={16} mt={32}>
            <Button variant="error-beelajar" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={loading} type="submit">
              {type !== 'edit' ? 'Submit' : 'Update'}
            </Button>
          </Flex>
        </form>
      </section>
    </>
  );
};

export default FormMission;
